import { Button, Divider, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import ToolbarPopover from '~/components/toolbar/shared/ToolbarPopover';
import { useAcquiredPrimarySliceShown } from '~/hooks/environment/useEnvironmentViews';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    boundingRoiConstraintState,
    collisionConstraintState,
    contourAutoSuggestionEnabledState,
} from '~/state/atoms/toolsAtoms';
import InsideConstraintSection, {
    pickDefaultBoundingRoi,
} from '../drawingConstraints/InsideConstraintSection';
import OutsideConstraintSection from '../drawingConstraints/OutsideConstraintSection';
import IntensityThresholdSection from '../drawingConstraints/IntensityThresholdSection';
import { useIntensityThreshold } from '../drawingConstraints/useIntensityThreshold';
import {
    mutedNoteSx,
    sectionDividerSx,
    warningSx,
} from '../drawingConstraints/constraintStyles';
import { ConstraintToggleRow } from '../drawingConstraints/constraintParts';
import {
    computeApprovedProtectionState,
    dropBoundingFromProtected,
    pruneProtectedToExisting,
    toggleProtectAllApproved,
} from '../drawingConstraints/collisionSelection';

// Re-exported so existing importers (and the selection-logic unit test) keep
// resolving it from this module.
export { pickDefaultBoundingRoi };

interface DrawingConstraintsControlProps {
    /** Disable interaction (e.g. no editable ROI selected). */
    disabled?: boolean;
    /**
     * Brush erase is active. Inside/Outside are persisted but do not apply to
     * an erase stroke (the engine short-circuits them), so the sections show a
     * "doesn't apply while erasing" note instead of pretending to be live.
     */
    erasing?: boolean;
    /**
     * Whether the Auto-suggest row applies in the current tool/mode. It is only
     * honoured for the brush in a draw mode, so it is hidden otherwise (e.g. the
     * line tray, or brush erase) rather than shown as an inert control. When
     * shown, the switch is operable only while a tile of the edit target
     * shows the image in its acquisition plane, the only plane a suggestion
     * can be made on.
     */
    showAutoSuggest?: boolean;
}

/**
 * Unified "drawing constraints" surface: a single toolbar button that opens a
 * popover with the per-stroke drawing modifiers.
 *
 *  - Inside (bounding): clip drawing to the interior of one structure.
 *  - Outside (collision): subtract a set of protected structures from the
 *    stroke so it cannot overwrite a neighbour.
 *  - Threshold (intensity): keep drawing only on voxels within an intensity
 *    range (HU for CT, SUVbw for PET).
 *
 * Each publishes to recoil. The armed tool takes them, and the draw action
 * applies them as one SDF step at commit. This component owns the shared
 * Inside/Outside state and cross-constraint invariants; each section renders
 * from props (Threshold via {@link useIntensityThreshold}).
 */
export default function DrawingConstraintsControl({
    disabled = false,
    erasing = false,
    showAutoSuggest = false,
}: DrawingConstraintsControlProps) {
    const intl = useIntl();
    const anchorRef = useRef<HTMLButtonElement | null>(null);
    const [open, setOpen] = useState(false);

    const [bounding, setBounding] = useRecoilState(boundingRoiConstraintState);
    const [collision, setCollision] = useRecoilState(collisionConstraintState);
    const [autoSuggest, setAutoSuggest] = useRecoilState(
        contourAutoSuggestionEnabledState
    );
    const {
        environment,
        structureSet,
        roiNumber: selectedRoiNumber,
    } = useActiveEditTarget();
    // A suggestion is derived from the neighbouring slice along the image Z
    // axis, so it needs a tile of the edit target that shows the image in
    // its acquisition plane. A rotated frame or an obliquely acquired image
    // leaves no such tile; the switch keeps its value through that, since
    // the first case is transient.
    const autoSuggestUnavailable = !useAcquiredPrimarySliceShown(environment);

    const threshold = useIntensityThreshold();

    // Candidate structures for both constraints. Excluded:
    //  - point ROIs (markers, isocenters, …) — no SDF texture to clip against;
    //  - the currently-selected (target) structure — clipping a structure to
    //    itself, or subtracting it from its own stroke, is meaningless (the
    //    engine skips it too), so it must not be offered as an option.
    const candidates = useMemo(
        () =>
            (structureSet?.roiArray ?? []).filter(
                (r) => !r.isPointRoi && r.roiNumber !== selectedRoiNumber
            ),
        [structureSet, selectedRoiNumber]
    );

    // Keep the bounding selection valid + auto-populate the default when the
    // Inside constraint is enabled.
    useEffect(() => {
        if (!bounding.enabled) {
            return;
        }
        const stillValid =
            bounding.roiNumber !== null &&
            candidates.some((r) => r.roiNumber === bounding.roiNumber);
        if (!stillValid) {
            setBounding((c) => ({
                ...c,
                roiNumber: pickDefaultBoundingRoi(candidates),
            }));
        }
    }, [bounding.enabled, bounding.roiNumber, candidates, setBounding]);

    const anyEnabled =
        bounding.enabled ||
        collision.enabled ||
        (threshold.enabled && threshold.available);

    // The structure currently acting as the "Inside" bound. It must not also
    // be a protected "Outside" structure — clipping the stroke to a structure
    // and then subtracting that same structure would erase the whole stroke —
    // so it is disabled in the Outside list (and removed from the set below).
    const activeBoundingRoi = bounding.enabled ? bounding.roiNumber : null;

    // Drop the active Inside structure from the protected set if it crept in
    // (e.g. it was checked first, then chosen as Inside, or via "protect all").
    // See dropBoundingFromProtected for the self-erase rationale.
    useEffect(() => {
        const next = dropBoundingFromProtected(
            collision.roiNumbers,
            activeBoundingRoi
        );
        if (next !== collision.roiNumbers) {
            setCollision((c) => ({ ...c, roiNumbers: next }));
        }
    }, [activeBoundingRoi, collision.roiNumbers, setCollision]);

    // Prune protected roiNumbers that no longer exist in the active structure
    // set. roiNumbers are unique only within a set, so without this a stale
    // selection (e.g. an ROI deleted mid-session) could silently protect the
    // wrong structure. Symmetric to the bounding revalidation above.
    useEffect(() => {
        const next = pruneProtectedToExisting(
            collision.roiNumbers,
            (structureSet?.roiArray ?? []).map((r) => r.roiNumber)
        );
        if (next !== collision.roiNumbers) {
            setCollision((c) => ({ ...c, roiNumbers: next }));
        }
    }, [structureSet, collision.roiNumbers, setCollision]);

    const handleToggleInside = useCallback(
        (enabled: boolean) => setBounding((c) => ({ ...c, enabled })),
        [setBounding]
    );
    const handleSelectInside = useCallback(
        (roiNumber: number) => setBounding((c) => ({ ...c, roiNumber })),
        [setBounding]
    );

    const handleToggleOutside = useCallback(
        (enabled: boolean) => setCollision((c) => ({ ...c, enabled })),
        [setCollision]
    );
    const handleToggleProtected = useCallback(
        (roiNumber: number, checked: boolean) =>
            setCollision((c) => ({
                ...c,
                roiNumbers: checked
                    ? [...c.roiNumbers, roiNumber]
                    : c.roiNumbers.filter((n) => n !== roiNumber),
            })),
        [setCollision]
    );
    const approvedRoiNumbers = useMemo(
        () =>
            candidates
                .filter(
                    (r) => r.isApproved && r.roiNumber !== activeBoundingRoi
                )
                .map((r) => r.roiNumber),
        [candidates, activeBoundingRoi]
    );
    const { hasApproved, allApprovedProtected, someApprovedProtected } =
        computeApprovedProtectionState(
            approvedRoiNumbers,
            collision.roiNumbers
        );

    // "Protect all approved" checkbox: add (checked) or remove (unchecked)
    // every approved structure from the protected set, leaving any
    // manually-checked non-approved structures untouched.
    const handleToggleProtectAllApproved = useCallback(
        (checked: boolean) =>
            setCollision((c) => ({
                ...c,
                roiNumbers: toggleProtectAllApproved(
                    c.roiNumbers,
                    approvedRoiNumbers,
                    checked
                ),
            })),
        [approvedRoiNumbers, setCollision]
    );

    // Enabled-but-inactive states: an enabled constraint with nothing it can
    // actually apply. Surfaced so an enabled protection never appears to be
    // doing nothing (the engine also logs the unresolved ROI).
    const insideInactive =
        bounding.enabled &&
        !candidates.some((r) => r.roiNumber === bounding.roiNumber);
    const outsideInactive =
        collision.enabled &&
        !collision.roiNumbers.some((n) =>
            candidates.some((r) => r.roiNumber === n)
        );

    return (
        <>
            <Tooltip
                title={intl.formatMessage({ id: 'constraintsTooltip' })}
                placement="bottom"
            >
                <Button
                    ref={anchorRef}
                    onClick={() => setOpen((o) => !o)}
                    disabled={disabled}
                    disableRipple
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    data-testid="tool-drawing-constraints"
                    sx={(theme) => ({
                        // Clear separation from the tool's own options
                        // (e.g. the brush-size slider) to the left.
                        ml: 2,
                        minWidth: 0,
                        // No fixed height: render as a compact text label so
                        // it shares the row's baseline with the slider / "cm"
                        // readout (the tray centers all items vertically).
                        px: 0.75,
                        py: 0,
                        fontSize: 11,
                        lineHeight: 1,
                        textTransform: 'none',
                        borderRadius: `${theme.shape.borderRadius}px`,
                        color:
                            open || anyEnabled
                                ? theme.palette.primary.main
                                : 'rgba(255, 255, 255, 0.85)',
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                        },
                    })}
                >
                    <FormattedMessage id="constraintsButtonLabel" />
                </Button>
            </Tooltip>
            <ToolbarPopover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                width={240}
            >
                {/* Auto-suggest is only honoured for the brush in a draw mode
                    (see useContourAutoSuggestionController). Hide the row where
                    it would do nothing (line tray, brush erase) rather than
                    leave a live-looking toggle that has no effect. */}
                {showAutoSuggest && (
                    <>
                        <ConstraintToggleRow
                            labelId="sliceDeformSuggest"
                            tooltipId="sliceDeformSuggestTooltip"
                            checked={autoSuggest}
                            disabled={autoSuggestUnavailable}
                            onToggle={setAutoSuggest}
                        />
                        {autoSuggestUnavailable && (
                            <Typography
                                sx={autoSuggest ? warningSx : mutedNoteSx}
                            >
                                <FormattedMessage id="sliceDeformSuggestNeedsTransversalSlice" />
                            </Typography>
                        )}

                        <Divider sx={sectionDividerSx} />
                    </>
                )}

                <InsideConstraintSection
                    enabled={bounding.enabled}
                    roiNumber={bounding.roiNumber}
                    candidates={candidates}
                    inactive={insideInactive}
                    erasing={erasing}
                    onToggle={handleToggleInside}
                    onSelect={handleSelectInside}
                />

                <Divider sx={sectionDividerSx} />

                <OutsideConstraintSection
                    enabled={collision.enabled}
                    candidates={candidates}
                    activeBoundingRoi={activeBoundingRoi}
                    protectedRoiNumbers={collision.roiNumbers}
                    hasApproved={hasApproved}
                    allApprovedProtected={allApprovedProtected}
                    someApprovedProtected={someApprovedProtected}
                    inactive={outsideInactive}
                    erasing={erasing}
                    onToggle={handleToggleOutside}
                    onToggleProtected={handleToggleProtected}
                    onToggleProtectAllApproved={handleToggleProtectAllApproved}
                />

                <Divider sx={sectionDividerSx} />

                <IntensityThresholdSection {...threshold} />
            </ToolbarPopover>
        </>
    );
}
